import React, { useMemo, useRef } from 'react';
import { useEnteredView } from '@fiverr-private/hooks';
import { trackCarouselEndOfFeed } from '../../../utils/events/mixpanel/events/carouselEndOfFeed';
import { trackCarouselShowMore } from '../../../utils/events/mixpanel/events/carouselShowMore';
import { trackCarouselImpression } from '../../../utils/events/mixpanel/events/carouselImpression';
import type { CarouselBaseProps } from '../../../utils/events/mixpanel/events/types';
import { InspireCarousel } from '../../InspireCarousel';
import {
  DEFAULT_SKIP,
  DEFAULT_RELATED_SUB_CATEGORY_IDS,
  DEFAULT_INSPIRE_DELIVERY_IDS_SAVED,
  DEFAULT_SHOULD_HIDE_SAVED_DELIVERIES,
  DEFAULT_MIXPANEL_RECOMMENDATIONS_ENABLED,
} from '../constants';
import { useRecommendedInspireDeliveries } from '../hooks/useRecommendedInspireDeliveries';
import type { RecommendationsInspireCarouselProps } from './types';

export const RecommendationsInspireCarousel = ({
  skip = DEFAULT_SKIP,
  relatedSubCategoryIds = DEFAULT_RELATED_SUB_CATEGORY_IDS,
  inspireDeliveryIdsSaved = DEFAULT_INSPIRE_DELIVERY_IDS_SAVED,
  shouldHideSavedDeliveries = DEFAULT_SHOULD_HIDE_SAVED_DELIVERIES,
  mixpanelRecommendationsEnabled = DEFAULT_MIXPANEL_RECOMMENDATIONS_ENABLED,
  mixpanelRecommendationsOptions,
  onSeenAll: onSeenAllFromProps,
  onShowMore: onShowMoreFromProps,
  ...rest
}: RecommendationsInspireCarouselProps) => {
  if (mixpanelRecommendationsEnabled && !mixpanelRecommendationsOptions) {
    throw new TypeError(
      'RecommendationsInspireCarousel: must provide "mixpanelRecommendationsOptions" when [mixpanelRecommendationsEnabled=true]'
    );
  }

  const { inspireDeliveries: inspireDeliveriesRecommended, isLoading } = useRecommendedInspireDeliveries(
    { relatedSubCategoryIds },
    skip
  );

  const inspireDeliveriesFiltered = useMemo(() => {
    return inspireDeliveriesRecommended.filter(({ id }) => !inspireDeliveryIdsSaved.includes(id));
  }, [inspireDeliveriesRecommended, inspireDeliveryIdsSaved]);

  const mixpanelProps: CarouselBaseProps = {
    pageName: mixpanelRecommendationsOptions?.pageName || '',
    carouselName: mixpanelRecommendationsOptions?.carouselName || '',
    subCategoryIds: relatedSubCategoryIds,
    numberOfSavedDeliveries: shouldHideSavedDeliveries // says how many saved deliveries are displayed on the carousel
      ? 0
      : inspireDeliveriesRecommended.length - inspireDeliveriesFiltered.length,
  };

  const onSeenAll = () => {
    if (onSeenAllFromProps) {
      onSeenAllFromProps();
    }

    if (!mixpanelRecommendationsEnabled) {
      return;
    }

    trackCarouselEndOfFeed(mixpanelProps);
  };

  const onShowMore = () => {
    if (onShowMoreFromProps) {
      onShowMoreFromProps();
    }

    if (!mixpanelRecommendationsEnabled) {
      return;
    }

    trackCarouselShowMore(mixpanelProps);
  };

  const containerRef = useRef<HTMLDivElement>(null);
  useEnteredView(containerRef, {
    threshold: 0.3,
    onEntered: () => {
      if (!mixpanelRecommendationsEnabled) {
        return;
      }

      trackCarouselImpression(mixpanelProps);
    },
  });

  const inspireDeliveries = shouldHideSavedDeliveries ? inspireDeliveriesFiltered : inspireDeliveriesRecommended;

  return (
    <div ref={containerRef}>
      <InspireCarousel
        inspireDeliveries={inspireDeliveries}
        isLoading={isLoading}
        onSeenAll={onSeenAll}
        onShowMore={onShowMore}
        {...rest}
      />
    </div>
  );
};
